import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, BarChart3, Megaphone, X } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import AiBadge from "./AiBadge";

const STORAGE_KEY = "inateb_cookie_consent";
const CONSENT_VERSION = 1;

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

// Globales Event, mit dem der Footer-Link die Einstellungen erneut öffnet
export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent("open-cookie-settings"));
}

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
      checked ? "bg-blue-brand" : "bg-gray-300"
    } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-5" : "translate-x-0.5"
      }`}
    />
  </button>
);

const CategoryRow = ({ icon: IconCmp, title, description, checked, onChange, disabled, badge }) => (
  <div className="flex items-start justify-between gap-4 py-4 border-b border-line last:border-0">
    <div className="flex gap-3">
      <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-brand flex items-center justify-center shrink-0">
        <IconCmp size={18} />
      </div>
      <div>
        <div className="flex items-center gap-2">
          <p className="font-head font-bold text-ink text-sm">{title}</p>
          {badge && (
            <span className="text-[10px] uppercase tracking-wider font-semibold text-blue-brand bg-blue-50 px-2 py-0.5 rounded-full">
              {badge}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed mt-1">{description}</p>
      </div>
    </div>
    <div className="pt-1">
      <Toggle checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  </div>
);

export default function CookieConsent() {
  const { t } = useI18n();
  const c = t.cookie;
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ statistics: false, marketing: false });

  useEffect(() => {
    const consent = readConsent();
    if (!consent) {
      setVisible(true);
    }
    const handler = () => {
      const current = readConsent();
      if (current) {
        setPrefs({ statistics: !!current.statistics, marketing: !!current.marketing });
      }
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener("open-cookie-settings", handler);
    return () => window.removeEventListener("open-cookie-settings", handler);
  }, []);

  const persist = useCallback((choice) => {
    const data = {
      necessary: true,
      statistics: !!choice.statistics,
      marketing: !!choice.marketing,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      // localStorage ggf. nicht verfügbar
    }
    setVisible(false);
    setShowSettings(false);
  }, []);

  const acceptAll = () => persist({ statistics: true, marketing: true });
  const rejectAll = () => persist({ statistics: false, marketing: false });
  const saveSelection = () => persist(prefs);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" data-testid="cookie-consent">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" />

      <div className="relative w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-line max-h-[92vh] overflow-y-auto">
        <AiBadge className="absolute top-3 right-3" />
        <div className="p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-brand flex items-center justify-center shrink-0">
              <Cookie size={24} />
            </div>
            <div className="flex-1">
              <h2 className="font-head font-extrabold text-ink text-xl sm:text-2xl">
                {c.title}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                {c.intro}
                <Link to="/datenschutz" className="text-blue-brand underline hover:no-underline" onClick={() => setVisible(false)}>
                  {c.privacyLink}
                </Link>
                {c.and}
                <Link to="/impressum" className="text-blue-brand underline hover:no-underline" onClick={() => setVisible(false)}>
                  {c.imprintLink}
                </Link>
                {c.end}
              </p>
            </div>
            <button
              type="button"
              aria-label={c.closeAria}
              onClick={rejectAll}
              className="text-muted-foreground hover:text-ink transition-colors shrink-0"
            >
              <X size={20} />
            </button>
          </div>

          {showSettings && (
            <div className="mt-6 rounded-xl border border-line px-4 sm:px-5" data-testid="cookie-settings">
              <CategoryRow
                icon={ShieldCheck}
                title={c.necessaryTitle}
                badge={c.necessaryBadge}
                description={c.necessaryDesc}
                checked={true}
                disabled={true}
                onChange={() => {}}
              />
              <CategoryRow
                icon={BarChart3}
                title={c.statsTitle}
                description={c.statsDesc}
                checked={prefs.statistics}
                onChange={(v) => setPrefs((p) => ({ ...p, statistics: v }))}
              />
              <CategoryRow
                icon={Megaphone}
                title={c.marketingTitle}
                description={c.marketingDesc}
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              />
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              data-testid="cookie-accept-all"
              onClick={acceptAll}
              className="order-1 sm:order-3 flex-1 bg-blue-brand text-white font-head font-semibold rounded-lg px-5 py-3 hover:bg-blue-700 transition-colors"
            >
              {c.acceptAll}
            </button>
            <button
              type="button"
              data-testid="cookie-reject-all"
              onClick={rejectAll}
              className="order-2 flex-1 border border-line text-ink font-head font-semibold rounded-lg px-5 py-3 hover:bg-gray-50 transition-colors"
            >
              {c.rejectAll}
            </button>
            {!showSettings ? (
              <button
                type="button"
                data-testid="cookie-open-settings"
                onClick={() => setShowSettings(true)}
                className="order-3 sm:order-1 flex-1 border border-line text-ink font-head font-semibold rounded-lg px-5 py-3 hover:bg-gray-50 transition-colors"
              >
                {c.settings}
              </button>
            ) : (
              <button
                type="button"
                data-testid="cookie-save-selection"
                onClick={saveSelection}
                className="order-3 sm:order-1 flex-1 border border-line text-ink font-head font-semibold rounded-lg px-5 py-3 hover:bg-gray-50 transition-colors"
              >
                {c.save}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
